import { AnimatePresence, motion } from "framer-motion"
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react"
import { createPortal } from "react-dom"
import { PromptFieldLoadingFrame } from "@/components/prompt-editor/prompt-field-loading-frame"
import type { JSONContent } from "@tiptap/core"
import { History, Loader2 } from "lucide-react"
import { usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/primitives"
import type { CameraPickerPreset } from "@/components/cameras/camera-picker-dialog"
import { PromptEditor, type PromptEditorHandle } from "@/components/prompt-editor/prompt-editor"
import { VoiceDictationButton } from "@/components/voice/voice-dictation-button"
import {
  buildMentionItems,
  type CastRow,
  type LocationRow,
  type PropRow,
} from "@/components/prompt-editor/mention-sources"
import { mentionKey, type MentionItem } from "@/components/prompt-editor/types"
import { confirmChoice } from "@/lib/confirm"
import { runWorkspace } from "@/lib/inertia-actions"
import { effectiveStoryboardImageProvider, effectiveVideoGenerationProvider } from "@/lib/provider-labels"
import type { StoryboardAnimaticRefFrame } from "@/lib/inertia-actions"
import {
  createAnimaticSegmentTranscription,
  fetchProjectCast,
  fetchProjectLocations,
  fetchProjectProps,
  fetchShotWorkspaceDetail,
  patchShotPromptSettings,
  patchShotWorkspacePrompts,
  uploadShotStoryboard,
  type PatchShotPromptSettingsBody,
  type PatchShotWorkspacePromptsBody,
  type ProjectCastRow,
} from "@/lib/workspace-api"
import { getCachedAvailableCameraPresets, fetchAvailableCameraPresets } from "@/lib/camera-presets-api"
import { ProjectScaleSheetModal } from "@/components/characters/project-scale-sheet-modal"
import { PropScaleSheetModal } from "@/components/props/prop-scale-sheet-modal"
import { MixedScaleSheetModal } from "@/components/scale-sheet/mixed-scale-sheet-modal"
import { ShotAnimaticTranscriptionExtras } from "@/components/shot-panel/shot-animatic-transcription-extras"
import { ShotStoryboardGenerationExtras } from "@/components/shot-panel/shot-storyboard-generation-extras"
import {
  ShotVideoGenerationExtras,
  type SeedanceAnimaticRefMode,
} from "@/components/shot-panel/shot-video-generation-extras"
import type { ShotReferenceInputRow } from "@/lib/shot-reference-inputs-api"
import { ShotReferenceInputsPanel } from "@/components/shot-reference-inputs-panel"
import { ReferenceInputThumb } from "@/components/reference-inputs/reference-input-thumb"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { SketchDialog, type SketchCorrectionPendingChange } from "@/components/sketch/sketch-dialog"
import { enqueueSave } from "@/components/shot-panel/enqueue-save"
import { PromptVersionsDialog } from "@/components/shot-panel/prompt-versions-dialog"
import { ShotCameraSelect } from "@/components/shot-panel/shot-camera-select"
import {
  SHOOTING_BOARD_PANEL_COUNT_MIN,
  ShootingBoardPanelCountPicker,
  clampShootingBoardPanelCount,
} from "@/components/shot-panel/shooting-board-panel-count-picker"
import { useShotPanelStore, type PrevizDepthVariant } from "@/stores/shot-panel-store"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import type { ReferenceImage } from "@/components/shot-references-panel"
import { cn } from "@/lib/utils"
import { sendStoryboardFrameFromExtras } from "@/lib/shot-llm-extras"
import { effectiveShotVideoModelKey, findShotVideoModel } from "@/lib/shot-video-settings"
import { maxVideoRefSeconds, maxVideoRefSecondsForProject } from "@/lib/video-duration"
import type { WorkspaceBaseProps } from "@/types/workspace"

type PageCatalogProps = Partial<WorkspaceBaseProps> & {
  project_cast?: ProjectCastRow[]
  project_locations?: {
    id: number
    name: string
    setting_type?: string | null
    reference_sheet_url?: string | null
  }[]
  project_props?: PropRow[]
}

function castRowsFromPage(rows: ProjectCastRow[]): CastRow[] {
  return rows.map((c) => ({
    id: c.id,
    name: c.name,
    role: c.role,
    reference_sheet_url: c.reference_sheet_url,
    reference_display_url: c.reference_display_url,
    reference_download_url: c.reference_download_url,
    portrait_thumb_url: c.portrait_thumb_url,
    portrait_url: c.portrait_url,
    portrait_download_url: c.portrait_download_url,
    active_voice_id: c.active_voice_id,
    voice_ready: c.voice_ready,
    voice_provider_kind: c.voice_provider_kind,
  }))
}

function locationRowsFromPage(rows: NonNullable<PageCatalogProps["project_locations"]>): LocationRow[] {
  return rows.map((l) => ({
    id: l.id,
    name: l.name,
    setting_type: l.setting_type ?? null,
    reference_sheet_url: l.reference_sheet_url ?? null,
  }))
}

function propRowsFromPage(rows: PropRow[]): PropRow[] {
  return rows.map((p) => ({
    id: p.id,
    name: p.name,
    category: p.category,
    reference_sheet_url: p.reference_sheet_url,
    front_view_thumb_url: p.front_view_thumb_url ?? null,
    thumb_url: p.thumb_url ?? null,
    has_front_view: p.has_front_view ?? false,
    states: (p.states ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      reference_url: s.reference_url,
    })),
  }))
}

async function fetchProjectPropRows(projectId: number): Promise<PropRow[]> {
  const props = await fetchProjectProps(projectId)
  return propRowsFromPage(
    props.map((p) => ({
      id: p.id,
      name: p.name,
      category: p.category,
      reference_sheet_url: p.reference_sheet_url,
      front_view_thumb_url: p.front_view_thumb_url ?? null,
      thumb_url: p.thumb_url ?? null,
      has_front_view: p.has_front_view ?? false,
      states: p.states.map((s) => ({ id: s.id, name: s.name, reference_url: s.reference_url })),
    })),
  )
}

type StoryboardPromptShot = {
  id: number
  number: string
  position?: number
  storyboard_system_prompt: string | null
  storyboard_image_prompt: string | null
  storyboard_image_prompt_default: string
  storyboard_system_prompt_default: string
  shooting_board_panel_count: number
  shooting_board_image_prompt: string | null
  shooting_board_image_prompt_default: string
  shooting_board_image_prompt_doc: JSONContent | null
  storyboard_location_block: string | null
  storyboard_characters_block: string | null
  storyboard_props_block: string | null
  video_kling_prompt: string | null
  video_kling_prompt_default: string
  video_system_prompt: string | null
  video_system_prompt_default: string
  storyboard_image_prompt_doc: JSONContent | null
  video_kling_prompt_doc: JSONContent | null
  character_ids: number[]
  scale_applicable?: boolean
  scale_sheet_applicable?: boolean
  scale_sheet_figures_incomplete?: boolean
  video_consider_scale?: boolean
  prop_scale_applicable?: boolean
  prop_scale_sheet_applicable?: boolean
  prop_scale_sheet_figures_incomplete?: boolean
  video_consider_prop_scale?: boolean
  mixed_scale_applicable?: boolean
  mixed_scale_sheet_applicable?: boolean
  mixed_scale_sheet_figures_incomplete?: boolean
  location_id: number | null
  props?: { id: number; name: string; state_id?: number | null; state_name?: string | null }[]
  seedance_video_seed: number | null
  storyboard_preview_url: string | null
  storyboard_full_url: string | null
  storyboard_download_url: string | null
  reference_images: ReferenceImage[]
  shot_reference_inputs: {
    storyboard: ShotReferenceInputRow[]
    video: ShotReferenceInputRow[]
    audio: ShotReferenceInputRow[]
    voice: ShotReferenceInputRow[]
  }
  llm_extras?: Record<string, unknown> | null
  active_video_timeline_markers?: { id: number; mark_ms: number }[]
  shooting_board_preview_url?: string | null
  shooting_board_version_count?: number
  shot_kind?: string | null
  camera_preset_name?: string | null
  animatic_segment?: {
    id: number
    position: number
    start_ms: number
    end_ms: number
    poster_url?: string | null
    poster_status?: string
    first_frame_url?: string | null
    first_frame_status?: string
    last_frame_url?: string | null
    last_frame_status?: string
    reference_clip_status?: string
    reference_clip_url?: string | null
    reference_clip_with_audio_status?: string
    reference_clip_with_audio_url?: string | null
    reference_audio_status?: string
    reference_audio_url?: string | null
  } | null
  /** Привязанный превиз шота — для чекбокса «Отправить превиз» в генерации видео. */
  previz?: {
    id: number
    name: string
    status: string
    duration_ms: number | null
    depth_status?: "pending" | "processing" | "ready" | "failed" | null
    depth_current?: boolean
  } | null
}

const SEEDANCE_ANIMATIC_REF_MODES: SeedanceAnimaticRefMode[] = ["video_silent", "video_with_audio", "audio_only"]

type Props = {
  projectId: number
  shotId: number
  onDataChanged: () => void
  /** Bump to soft-refresh media fields without resetting prompt editors. */
  refreshKey?: number
  composerKind: "storyboard" | "video" | "shooting_board"
  extrasHost: HTMLElement | null
  composerHost: HTMLElement | null
  /** Prompt editor fills leftover column height (shot cover / mobile composer). */
  fillColumn?: boolean
  /** Fires when storyboard/video mention catalogs are ready (for agent saved-prompt chips). */
  onMentionItemsChange?: (items: MentionItem[]) => void
  /** First prompt fetch finished (success or error). */
  onInitialLoadSettled?: () => void
  /** Переопределение видеомодели шота. Пусто — модель проекта. */
  shotVideoModelKey?: string | null
}

export type StoryboardPromptViewHandle = {
  flushPromptSave: () => Promise<boolean>
  /** Apply agent-improved prompt text into the local editors without a page reload. */
  applyExternalPrompt: (payload: {
    target: "image" | "video" | "shooting_board"
    text: string
    cameraPresetIds?: number[]
    panelCount?: number
  }) => void
  applyExternalSettings: (payload: {
    sendAnimatic?: boolean
    animaticMode?: string
    useShootingBoard?: boolean
  }) => void
  /** Previz / animatic exist but their send checkbox is off: ask, then persist «with». */
  confirmMotionReferences: () => Promise<boolean>
}

type ExternalPromptTarget = "image" | "video" | "shooting_board"

function composerKindForExternalPrompt(target: ExternalPromptTarget): "storyboard" | "video" | "shooting_board" {
  switch (target) {
    case "image":
      return "storyboard"
    default:
      return target
  }
}

function norm(s: string) {
  return s.replace(/\r\n/g, "\n").trim()
}

/** Scale applicability + consider flags from shot workspace detail (shared by load + soft refresh). */
function readScaleFieldsFromDetail(s: Record<string, unknown>): {
  character_ids: number[]
  props: StoryboardPromptShot["props"]
  scale_applicable: boolean
  scale_sheet_applicable: boolean
  scale_sheet_figures_incomplete: boolean
  video_consider_scale: boolean
  prop_scale_applicable: boolean
  prop_scale_sheet_applicable: boolean
  prop_scale_sheet_figures_incomplete: boolean
  video_consider_prop_scale: boolean
  mixed_scale_applicable: boolean
  mixed_scale_sheet_applicable: boolean
  mixed_scale_sheet_figures_incomplete: boolean
} {
  const extras = s.llm_extras
  const extrasObj = extras && typeof extras === "object" ? (extras as Record<string, unknown>) : null
  const scaleApplicable = Boolean(s.scale_applicable)
  const propScaleApplicable = Boolean(s.prop_scale_applicable)
  const mixedScaleApplicable = Boolean(s.mixed_scale_applicable)
  let videoConsiderScaleOn = scaleApplicable
    ? extrasObj?.["video_consider_scale"] === undefined
      ? Boolean(s.video_consider_scale ?? true)
      : Boolean(extrasObj["video_consider_scale"])
    : false
  let videoConsiderPropScaleOn = propScaleApplicable
    ? extrasObj?.["video_consider_prop_scale"] === undefined
      ? Boolean(s.video_consider_prop_scale ?? true)
      : Boolean(extrasObj["video_consider_prop_scale"])
    : false
  if (mixedScaleApplicable) {
    // One mixed toggle: character flag is source of truth; prop mirrors it.
    // Do not gate on scale_applicable (≥2 heroes) — mixed is valid for 1+1.
    const unified =
      extrasObj?.["video_consider_scale"] === undefined
        ? Boolean(s.video_consider_scale ?? true)
        : Boolean(extrasObj["video_consider_scale"])
    videoConsiderScaleOn = unified
    videoConsiderPropScaleOn = unified
  }
  return {
    character_ids: (s.character_ids as number[] | undefined) ?? [],
    props: (s.props as StoryboardPromptShot["props"]) ?? [],
    scale_applicable: scaleApplicable,
    scale_sheet_applicable: Boolean(s.scale_sheet_applicable),
    scale_sheet_figures_incomplete: Boolean(s.scale_sheet_figures_incomplete),
    video_consider_scale: videoConsiderScaleOn,
    prop_scale_applicable: propScaleApplicable,
    prop_scale_sheet_applicable: Boolean(s.prop_scale_sheet_applicable),
    prop_scale_sheet_figures_incomplete: Boolean(s.prop_scale_sheet_figures_incomplete),
    video_consider_prop_scale: videoConsiderPropScaleOn,
    mixed_scale_applicable: mixedScaleApplicable,
    mixed_scale_sheet_applicable: Boolean(s.mixed_scale_sheet_applicable),
    mixed_scale_sheet_figures_incomplete: Boolean(s.mixed_scale_sheet_figures_incomplete),
  }
}

function storyboardModelForLabel(p: {
  storyboard_image_provider: string
  effective_storyboard_image_provider?: string | null
  effective_storyboard_image_model?: string | null
  dashscope_storyboard_image_model?: string | null
  google_gemini_image_model?: string | null
  openrouter_image_model?: string | null
  byteplus_seedream_image_model?: string | null
}) {
  if (p.effective_storyboard_image_model) return p.effective_storyboard_image_model

  switch (effectiveStoryboardImageProvider(p)) {
    case "dashscope_qwen_image":
    case "dashscope_wan_image":
      return p.dashscope_storyboard_image_model
    case "google_gemini_image":
      return p.google_gemini_image_model
    case "openrouter":
      return p.openrouter_image_model
    case "byteplus_seedream_image":
      return p.byteplus_seedream_image_model
    default:
      return null
  }
}

function getStoryboardEngineLabel(provider: string | undefined, model?: string | null) {
  switch (provider) {
    case "kling":
      return "Kling"
    case "n8n_gpt_image":
      return "GPT Image (n8n)"
    case "dashscope_qwen_image":
      return model ? `Qwen (${model})` : "Qwen Image"
    case "dashscope_wan_image":
      return model ? `WAN Image (${model})` : "WAN Image"
    case "openrouter":
      return model?.toString().trim() ? `OpenRouter (${model})` : "OpenRouter"
    case "google_gemini_image":
      return model ? `Gemini image (${model})` : "Gemini image"
    case "byteplus_seedream_image":
      return model ? `Seedream (${model})` : "Seedream"
    default:
      return provider ?? "—"
  }
}

function getVideoEngineLabel(provider: string | undefined) {
  switch (provider) {
    case "wan":
      return "WAN"
    case "openrouter_video":
    case "byteplus_seedance_video":
      return "Seedance"
    case "google_veo":
      return "Veo"
    default:
      return "Kling"
  }
}

export const StoryboardPromptView = forwardRef<StoryboardPromptViewHandle, Props>(function StoryboardPromptView(
  {
    projectId,
    shotId,
    onDataChanged,
    refreshKey = 0,
    composerKind,
    extrasHost,
    composerHost,
    fillColumn = false,
    onMentionItemsChange,
    shotVideoModelKey = null,
    onInitialLoadSettled,
  },
  ref,
) {
  const { t } = useTranslation(["shot", "common", "jobs", "workspace"])
  const page = usePage<PageCatalogProps>()
  const { project, sketch, generation_models_by_context } = useShotPanelWorkspace()
  const previewTab = useShotPanelStore((s) => s.previewTab)
  const pendingOpenMixedScaleSheet = useShotPanelStore((s) => s.pendingOpenMixedScaleSheet)
  const consumePendingOpenMixedScaleSheet = useShotPanelStore((s) => s.consumePendingOpenMixedScaleSheet)
  const previzDepthVariant = useShotPanelStore((s) => s.previzDepthVariant)
  const setPrevizDepthVariant = useShotPanelStore((s) => s.setPrevizDepthVariant)
  const storyboardEngineLabel = getStoryboardEngineLabel(
    project ? effectiveStoryboardImageProvider(project) : undefined,
    project ? storyboardModelForLabel(project) : null,
  )
  const videoEngineLabel = getVideoEngineLabel(project ? effectiveVideoGenerationProvider(project) : undefined)

  const [shot, setShot] = useState<StoryboardPromptShot | null>(null)
  const [sketchOpen, setSketchOpen] = useState(false)
  const [projectCast, setProjectCast] = useState<CastRow[]>([])
  const [projectLocations, setProjectLocations] = useState<LocationRow[]>([])
  const [projectProps, setProjectProps] = useState<PropRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [systemOpen, setSystemOpen] = useState(false)
  const [videoSystemOpen, setVideoSystemOpen] = useState(false)
  const [system, setSystem] = useState("")
  const [image, setImage] = useState("")
  const [includeLocation, setIncludeLocation] = useState(false)
  const [includeCharacters, setIncludeCharacters] = useState(false)
  const [includeProps, setIncludeProps] = useState(false)
  const [videoText, setVideoText] = useState("")
  const [videoDoc, setVideoDoc] = useState<JSONContent | null>(null)
  const [videoSystem, setVideoSystem] = useState("")
  const [shootingBoardText, setShootingBoardText] = useState("")
  const [shootingBoardDoc, setShootingBoardDoc] = useState<JSONContent | null>(null)
  const [transcribing, setTranscribing] = useState(false)
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null)
  const [transcriptionRefreshKey, setTranscriptionRefreshKey] = useState(0)
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle")
  const [promptHistoryKind, setPromptHistoryKind] = useState<null | "storyboard" | "video">(null)
  const [selectedPresetIds, setSelectedPresetIds] = useState<number[]>([])
  const [cameraPickerOpen, setCameraPickerOpen] = useState(false)
  const [loadedPresets, setLoadedPresets] = useState<CameraPickerPreset[]>(
    () => getCachedAvailableCameraPresets(projectId) ?? [],
  )
  const handleCameraPresetsLoaded = useCallback((presets: CameraPickerPreset[]) => {
    setLoadedPresets(presets)
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoadedPresets(getCachedAvailableCameraPresets(projectId) ?? [])
    fetchAvailableCameraPresets(projectId)
      .then((presets) => {
        if (!cancelled) setLoadedPresets(presets)
      })
      .catch(() => {
        // Keep cache/empty; mention chips fall back to @cam[id] until presets load.
      })
    return () => {
      cancelled = true
    }
  }, [projectId])

  const [sendAnimaticRef, setSendAnimaticRef] = useState(false)
  const [sendPrevizRef, setSendPrevizRef] = useState(false)
  const [sendPrevizDepth, setSendPrevizDepth] = useState(false)
  const [animaticRefMode, setAnimaticRefMode] = useState<SeedanceAnimaticRefMode>("video_silent")
  const [useShootingBoard, setUseShootingBoard] = useState(false)
  const [sendStoryboardFrame, setSendStoryboardFrame] = useState(true)
  const [videoConsiderScale, setVideoConsiderScale] = useState(true)
  const [videoConsiderPropScale, setVideoConsiderPropScale] = useState(true)
  const [sendCurrentAsRef, setSendCurrentAsRef] = useState(false)
  const [animaticRefFrame, setAnimaticRefFrame] = useState<StoryboardAnimaticRefFrame | null>(null)
  const [seedText, setSeedText] = useState("")
  const [framePreviewOpen, setFramePreviewOpen] = useState(false)
  const [storyboardSketchPendingJobId, setStoryboardSketchPendingJobId] = useState<number | null>(null)
  const [agentUpdatingTarget, setAgentUpdatingTarget] = useState<"image" | "video" | "shooting_board" | null>(null)
  const [scaleSheetOpen, setScaleSheetOpen] = useState(false)
  const [propScaleSheetOpen, setPropScaleSheetOpen] = useState(false)
  const [mixedScaleSheetOpen, setMixedScaleSheetOpen] = useState(false)
  const imageEditorRef = useRef<PromptEditorHandle>(null)
  const videoEditorRef = useRef<PromptEditorHandle>(null)
  const shootingBoardEditorRef = useRef<PromptEditorHandle>(null)
  const agentUpdateTimersRef = useRef<{ raf1?: number; raf2?: number; hold?: number }>({})
  /** Agent applied prompt while Frame/Video editor was unmounted (Assistant tab). */
  const pendingExternalPromptRef = useRef<{
    target: "image" | "video" | "shooting_board"
    text: string
    cameraPresetIds?: number[]
  } | null>(null)
  const savedSnapshot = useRef({
    system: "",
    image: "",
    videoText: "",
    videoSystem: "",
    shootingBoardText: "",
    presetIds: [] as number[],
    sendAnimaticRef: false,
    sendPrevizRef: false,
    sendPrevizDepth: false,
    previzDepthVariant: "gray" as PrevizDepthVariant,
    animaticRefMode: "video_silent" as SeedanceAnimaticRefMode,
    useShootingBoard: false,
    sendStoryboardFrame: true,
    videoConsiderScale: true,
    videoConsiderPropScale: true,
    sendCurrentAsRef: false,
    animaticRefFrame: null as StoryboardAnimaticRefFrame | null,
    seedText: "",
  })
  const extrasSaveSeqRef = useRef(0)
  const extrasSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const persistRef = useRef<((skipVersion: boolean) => Promise<boolean>) | null>(null)
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve())

  const isSeedanceVideo = videoEngineLabel === "Seedance"
  const supportsAnimaticReference = isSeedanceVideo || videoEngineLabel === "Kling"
  const maxAnimaticRefSeconds = project
    ? maxVideoRefSecondsForProject(project, generation_models_by_context?.shot_video)
    : 15
  const previzMaxSeconds = (() => {
    if (!project) return 15
    const key = effectiveShotVideoModelKey(shotVideoModelKey, project)
    const model = findShotVideoModel(key, generation_models_by_context?.shot_video)
    if (model) return maxVideoRefSeconds(model)
    return maxVideoRefSeconds({ id: key || undefined })
  })()
  const previzMaxSecondsRef = useRef(previzMaxSeconds)
  previzMaxSecondsRef.current = previzMaxSeconds
  const isByteplusSeedance = project ? effectiveVideoGenerationProvider(project) === "byteplus_seedance_video" : false

  // Превиз уходит референсом целиком, поэтому лимит — по его полной длительности.
  const previzDurationSec = useMemo(() => Math.max(0, (shot?.previz?.duration_ms ?? 0) / 1000), [shot?.previz])
  const previzRefTooLong = Boolean(shot?.previz && previzDurationSec > previzMaxSeconds)

  useEffect(() => {
    if (previzRefTooLong && sendPrevizRef) setSendPrevizRef(false)
  }, [previzRefTooLong, sendPrevizRef])

  // Аниматик и превиз — оба video-референсы движения камеры (@Video1): активен максимум один.
  const handleSendAnimaticRefChange = useCallback((value: boolean) => {
    setSendAnimaticRef(value)
    if (value) setSendPrevizRef(false)
  }, [])
  const handleSendPrevizRefChange = useCallback((value: boolean) => {
    setSendPrevizRef(value)
    if (value) setSendAnimaticRef(false)
  }, [])
  const scaleApplicable = shot?.scale_applicable ?? (shot?.character_ids?.length ?? 0) >= 2
  const propScaleApplicable = shot?.prop_scale_applicable ?? false
  const mixedScaleApplicable = Boolean(shot?.mixed_scale_applicable)
  const scaleSheetLinkEnabled = scaleApplicable && Boolean(shot?.scale_sheet_applicable)
  const propScaleSheetLinkEnabled = propScaleApplicable && Boolean(shot?.prop_scale_sheet_applicable)
  const mixedScaleSheetLinkEnabled = mixedScaleApplicable && Boolean(shot?.mixed_scale_sheet_applicable)

  // Job / deep-link #scale_sheet=mixed → open mixed scale sheet modal once shot is ready.
  useEffect(() => {
    if (!shot || loading) return
    if (!mixedScaleApplicable) return
    if (!pendingOpenMixedScaleSheet) return
    consumePendingOpenMixedScaleSheet()
    setMixedScaleSheetOpen(true)
  }, [shot, loading, mixedScaleApplicable, pendingOpenMixedScaleSheet, consumePendingOpenMixedScaleSheet])

  const handleTranscribe = useCallback(async () => {
    const segmentId = shot?.animatic_segment?.id
    if (!segmentId || transcribing) return
    setTranscribing(true)
    setTranscriptionError(null)
    try {
      const res = await createAnimaticSegmentTranscription(projectId, segmentId)
      if (!res.ok) {
        setTranscriptionError(res.error ?? t("common.transcriptionStartFailed"))
        return
      }
      setTranscriptionRefreshKey((k) => k + 1)
      onDataChanged()
    } catch (e) {
      setTranscriptionError(e instanceof Error ? e.message : String(e))
    } finally {
      setTranscribing(false)
    }
  }, [shot?.animatic_segment?.id, transcribing, projectId, onDataChanged, t])

  const availablePresets = loadedPresets

  const linkedCharacterIds = useMemo(() => shot?.character_ids ?? [], [shot?.character_ids])
  const linkedLocationIds = useMemo(() => (shot?.location_id != null ? [shot.location_id] : []), [shot?.location_id])
  const linkedPropAssignments = useMemo(
    () => (shot?.props ?? []).map((p) => ({ prop_id: p.id, state_id: p.state_id ?? null })),
    [shot?.props],
  )
  const linkedPropIds = useMemo(() => linkedPropAssignments.map((p) => p.prop_id), [linkedPropAssignments])
  const cameraMentionRows = useMemo(
    () =>
      availablePresets.map((p) => ({
        id: p.id,
        name: p.name,
        imageUrl: p.demo_poster_url || null,
      })),
    [availablePresets],
  )
  const storyboardMentionItems = useMemo(
    () =>
      buildMentionItems({
        characters: projectCast,
        locations: projectLocations,
        props: projectProps,
        references: shot?.shot_reference_inputs.storyboard,
        cameras: cameraMentionRows,
        linkedCharacterIds,
        linkedLocationIds,
        linkedPropAssignments,
      }),
    [
      projectCast,
      projectLocations,
      projectProps,
      shot?.shot_reference_inputs.storyboard,
      cameraMentionRows,
      linkedCharacterIds,
      linkedLocationIds,
      linkedPropAssignments,
    ],
  )
  const videoMentionItems = useMemo(
    () =>
      buildMentionItems({
        characters: projectCast,
        locations: projectLocations,
        props: projectProps,
        references: [...(shot?.shot_reference_inputs.video ?? []), ...(shot?.shot_reference_inputs.audio ?? [])].sort(
          (a, b) => a.position - b.position,
        ),
        cameras: cameraMentionRows,
        linkedCharacterIds,
        linkedLocationIds,
        linkedPropAssignments,
      }),
    [
      projectCast,
      projectLocations,
      projectProps,
      shot?.shot_reference_inputs.video,
      shot?.shot_reference_inputs.audio,
      cameraMentionRows,
      linkedCharacterIds,
      linkedLocationIds,
      linkedPropAssignments,
    ],
  )

  useEffect(() => {
    if (!onMentionItemsChange) return
    const byKey = new Map<string, MentionItem>()
    for (const item of [...storyboardMentionItems, ...videoMentionItems]) {
      byKey.set(mentionKey(item.category, item.id, item.stateId, item.attrKey), item)
    }
    onMentionItemsChange(Array.from(byKey.values()))
  }, [storyboardMentionItems, videoMentionItems, onMentionItemsChange])

  const onInitialLoadSettledRef = useRef(onInitialLoadSettled)
  onInitialLoadSettledRef.current = onInitialLoadSettled

  useEffect(() => {
    setShot(null)
    setProjectCast([])
    setProjectLocations([])
    setProjectProps([])
    setError(null)
    setLoading(true)
    setSaving(false)
    setSaveStatus("idle")
    setSystemOpen(false)
    setVideoSystemOpen(false)
    setSystem("")
    setImage("")
    setVideoText("")
    setVideoDoc(null)
    setVideoSystem("")
    setShootingBoardText("")
    setShootingBoardDoc(null)
    setIncludeLocation(false)
    setIncludeCharacters(false)
    setIncludeProps(false)
    setSelectedPresetIds([])
    setSendAnimaticRef(false)
    setAnimaticRefMode("video_silent")
    setUseShootingBoard(false)
    setSendStoryboardFrame(true)
    setSendCurrentAsRef(false)
    setAnimaticRefFrame(null)
    setScaleSheetOpen(false)
    setPropScaleSheetOpen(false)
    setSeedText("")

    let cancelled = false

    const pageProjectId = page.props.project?.id
    const pageCast =
      pageProjectId === projectId && Array.isArray(page.props.project_cast) ? page.props.project_cast : null
    const pageLocations =
      pageProjectId === projectId && Array.isArray(page.props.project_locations) ? page.props.project_locations : null
    const pageProps =
      pageProjectId === projectId && Array.isArray(page.props.project_props) ? page.props.project_props : null
    // Inertia seed paints mentions immediately; JSON refresh below keeps them fresh.
    const usedPageCatalogSeed = pageCast != null || pageLocations != null || pageProps != null

    const castPromise = pageCast
      ? Promise.resolve(castRowsFromPage(pageCast))
      : fetchProjectCast(projectId).then(castRowsFromPage)
    const locationsPromise = pageLocations
      ? Promise.resolve(locationRowsFromPage(pageLocations))
      : fetchProjectLocations(projectId).then(locationRowsFromPage)
    const propsPromise = pageProps ? Promise.resolve(propRowsFromPage(pageProps)) : fetchProjectPropRows(projectId)

    Promise.all([fetchShotWorkspaceDetail(projectId, shotId), castPromise, locationsPromise, propsPromise])
      .then(([detail, cast, locations, props]) => {
        if (cancelled) return

        setProjectCast(cast)
        setProjectLocations(locations)
        setProjectProps(props)

        if (usedPageCatalogSeed) {
          void Promise.all([
            fetchProjectCast(projectId).then(castRowsFromPage),
            fetchProjectLocations(projectId).then(locationRowsFromPage),
            fetchProjectPropRows(projectId),
          ])
            .then(([freshCast, freshLocations, freshProps]) => {
              if (cancelled) return
              setProjectCast(freshCast)
              setProjectLocations(freshLocations)
              setProjectProps(freshProps)
            })
            .catch(() => {
              // Keep Inertia seed; mentions stay usable.
            })
        }

        const { shot: s } = detail
        const shotKind = ((s as Record<string, unknown>).shot_kind as string) ?? "regular"
        const extras = (s as Record<string, unknown>).llm_extras
        const extrasObj = extras && typeof extras === "object" ? (extras as Record<string, unknown>) : null
        const rawStoryboardFrame = extrasObj?.["storyboard_animatic_reference_frame"]
        const loadedAnimaticFrame: StoryboardAnimaticRefFrame | null =
          rawStoryboardFrame === "first" || rawStoryboardFrame === "poster" || rawStoryboardFrame === "last"
            ? rawStoryboardFrame
            : null
        const sendCurrentRefOn = Boolean(extrasObj?.["storyboard_send_current_as_reference"])
        const animaticRefOn = Boolean(
          extras &&
          typeof extras === "object" &&
          (extras as Record<string, unknown>)["seedance_send_animatic_reference"],
        )
        const previzRefOn = Boolean(extrasObj?.["seedance_send_previz_reference"])
        const previzDepthOn = Boolean(extrasObj?.["seedance_send_previz_depth"])
        const previzDepthVariantLoaded: PrevizDepthVariant =
          extrasObj?.["seedance_previz_depth_variant"] === "vis" ? "vis" : "gray"
        const rawAnimaticMode = String(extrasObj?.["seedance_animatic_reference_mode"] ?? "")
        const animaticModeLoaded: SeedanceAnimaticRefMode = (SEEDANCE_ANIMATIC_REF_MODES as string[]).includes(
          rawAnimaticMode,
        )
          ? (rawAnimaticMode as SeedanceAnimaticRefMode)
          : "video_silent"
        const useSbFlag =
          extras && typeof extras === "object"
            ? Boolean((extras as Record<string, unknown>)["seedance_use_shooting_board"])
            : false
        const sendStoryboardFrameOn = sendStoryboardFrameFromExtras(extrasObj)
        const scaleFields = readScaleFieldsFromDetail(s as unknown as Record<string, unknown>)
        const row: StoryboardPromptShot = {
          id: s.id,
          number: s.number,
          storyboard_system_prompt: s.storyboard_system_prompt ?? null,
          storyboard_image_prompt: s.storyboard_image_prompt ?? null,
          storyboard_image_prompt_default: s.storyboard_image_prompt_default ?? "",
          storyboard_system_prompt_default: s.storyboard_system_prompt_default ?? "",
          shooting_board_panel_count: clampShootingBoardPanelCount(
            s.shooting_board_panel_count ?? SHOOTING_BOARD_PANEL_COUNT_MIN,
          ),
          shooting_board_image_prompt: s.shooting_board_image_prompt ?? null,
          shooting_board_image_prompt_default: s.shooting_board_image_prompt_default ?? "",
          shooting_board_image_prompt_doc:
            (s.shooting_board_image_prompt_doc as JSONContent | null | undefined) ?? null,
          storyboard_location_block:
            ((s as Record<string, unknown>).storyboard_location_block as string | null | undefined) ?? null,
          storyboard_characters_block:
            ((s as Record<string, unknown>).storyboard_characters_block as string | null | undefined) ?? null,
          storyboard_props_block:
            ((s as Record<string, unknown>).storyboard_props_block as string | null | undefined) ?? null,
          video_kling_prompt: s.video_kling_prompt ?? null,
          video_kling_prompt_default: s.video_kling_prompt_default ?? "",
          video_system_prompt: s.video_system_prompt ?? null,
          video_system_prompt_default: s.video_system_prompt_default ?? "",
          storyboard_image_prompt_doc: (s.storyboard_image_prompt_doc as JSONContent | null | undefined) ?? null,
          video_kling_prompt_doc: (s.video_kling_prompt_doc as JSONContent | null | undefined) ?? null,
          character_ids: scaleFields.character_ids,
          scale_applicable: scaleFields.scale_applicable,
          scale_sheet_applicable: scaleFields.scale_sheet_applicable,
          scale_sheet_figures_incomplete: scaleFields.scale_sheet_figures_incomplete,
          video_consider_scale: scaleFields.video_consider_scale,
          prop_scale_applicable: scaleFields.prop_scale_applicable,
          prop_scale_sheet_applicable: scaleFields.prop_scale_sheet_applicable,
          prop_scale_sheet_figures_incomplete: scaleFields.prop_scale_sheet_figures_incomplete,
          video_consider_prop_scale: scaleFields.video_consider_prop_scale,
          mixed_scale_applicable: scaleFields.mixed_scale_applicable,
          mixed_scale_sheet_applicable: scaleFields.mixed_scale_sheet_applicable,
          mixed_scale_sheet_figures_incomplete: scaleFields.mixed_scale_sheet_figures_incomplete,
          location_id: ((s as Record<string, unknown>).location_id as number | null | undefined) ?? null,
          props: scaleFields.props,
          seedance_video_seed:
            ((s as Record<string, unknown>).seedance_video_seed as number | null | undefined) ?? null,
          storyboard_preview_url: s.storyboard_preview_url ?? null,
          storyboard_full_url:
            ((s as Record<string, unknown>).storyboard_full_url as string | null | undefined) ?? null,
          storyboard_download_url:
            ((s as Record<string, unknown>).storyboard_download_url as string | null | undefined) ?? null,
          reference_images: ((s as Record<string, unknown>).reference_images as ReferenceImage[]) ?? [],
          shot_reference_inputs: ((s as Record<string, unknown>)
            .shot_reference_inputs as StoryboardPromptShot["shot_reference_inputs"]) ?? {
            storyboard: [],
            video: [],
            audio: [],
            voice: [],
          },
          llm_extras: extras && typeof extras === "object" ? (extras as Record<string, unknown>) : null,
          active_video_timeline_markers:
            ((s as Record<string, unknown>)
              .active_video_timeline_markers as StoryboardPromptShot["active_video_timeline_markers"]) ?? [],
          animatic_segment: (s as Record<string, unknown>).animatic_segment as StoryboardPromptShot["animatic_segment"],
          shooting_board_preview_url: (s as Record<string, unknown>).shooting_board_preview_url as
            | string
            | null
            | undefined,
          shooting_board_version_count: (s as Record<string, unknown>).shooting_board_version_count as
            | number
            | undefined,
          shot_kind: shotKind,
          previz: ((s as Record<string, unknown>).previz as StoryboardPromptShot["previz"]) ?? null,
        }
        const hasSb = Boolean(row.shooting_board_preview_url?.trim()) || (row.shooting_board_version_count ?? 0) > 0
        const sendAnimaticRefLoaded = animaticRefOn
        const sendPrevizRefLoaded =
          previzRefOn && row.previz != null && !((row.previz.duration_ms ?? 0) / 1000 > previzMaxSecondsRef.current)
        setShot(row)
        setSendAnimaticRef(sendAnimaticRefLoaded)
        setSendPrevizRef(sendPrevizRefLoaded)
        setSendPrevizDepth(previzDepthOn)
        setPrevizDepthVariant(previzDepthVariantLoaded)
        setAnimaticRefMode(animaticModeLoaded)
        setUseShootingBoard(hasSb && useSbFlag)
        setSendStoryboardFrame(sendStoryboardFrameOn)
        setVideoConsiderScale(scaleFields.video_consider_scale)
        setVideoConsiderPropScale(scaleFields.video_consider_prop_scale)
        setSendCurrentAsRef(sendCurrentRefOn)
        setAnimaticRefFrame(loadedAnimaticFrame)
        const fetchedIds = ((s as Record<string, unknown>).camera_preset_ids as number[]) ?? []
        setSelectedPresetIds(fetchedIds)
        const sysVal = norm(row.storyboard_system_prompt ?? row.storyboard_system_prompt_default)
        setSystem(sysVal)
        const imgText = norm(row.storyboard_image_prompt ?? row.storyboard_image_prompt_default)
        setImage(imgText)
        if (row.storyboard_location_block) {
          setIncludeLocation(imgText.includes(row.storyboard_location_block.trim()))
        }
        if (row.storyboard_characters_block) {
          setIncludeCharacters(imgText.includes(row.storyboard_characters_block.trim()))
        }
        if (row.storyboard_props_block) {
          setIncludeProps(imgText.includes(row.storyboard_props_block.trim()))
        }
        const vDef = norm(row.video_kling_prompt_default)
        const vCustom = row.video_kling_prompt?.trim()
        const vidText = norm(vCustom ?? vDef)
        setVideoText(vidText)
        setVideoDoc(row.video_kling_prompt_doc)
        const vsys = norm(row.video_system_prompt ?? row.video_system_prompt_default)
        setVideoSystem(vsys)
        const sbCustom = row.shooting_board_image_prompt?.trim()
        const sbText = norm(sbCustom ?? row.shooting_board_image_prompt_default)
        setShootingBoardText(sbText)
        setShootingBoardDoc(sbCustom ? row.shooting_board_image_prompt_doc : null)
        const seedInit = String(row.seedance_video_seed ?? row.id)
        setSeedText(seedInit)
        savedSnapshot.current = {
          system: sysVal,
          image: imgText,
          videoText: vidText,
          videoSystem: vsys,
          shootingBoardText: sbText,
          presetIds: fetchedIds,
          sendAnimaticRef: sendAnimaticRefLoaded,
          sendPrevizRef: sendPrevizRefLoaded,
          sendPrevizDepth: previzDepthOn,
          previzDepthVariant: previzDepthVariantLoaded,
          animaticRefMode: animaticModeLoaded,
          useShootingBoard: hasSb && useSbFlag,
          sendStoryboardFrame: sendStoryboardFrameOn,
          videoConsiderScale: scaleFields.video_consider_scale,
          videoConsiderPropScale: scaleFields.video_consider_prop_scale,
          sendCurrentAsRef: sendCurrentRefOn,
          animaticRefFrame: loadedAnimaticFrame,
          seedText: seedInit,
        }
        setLoading(false)
        onInitialLoadSettledRef.current?.()
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : t("common.loadError"))
        setLoading(false)
        onInitialLoadSettledRef.current?.()
      })

    return () => {
      cancelled = true
    }
    // Catalogs from Inertia are a one-shot fast path at open; including page.props
    // would re-run on every tree/jobs partial reload and reset prompt editors.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [shotId, projectId, t, maxAnimaticRefSeconds])

  const isTransition = (shot?.shot_kind ?? "regular") === "transition"

  // Маска устарела или её нет — не оставляем отправку карты, которую генерация отклонит.
  // Пока джоба считает карту, выбор держим: после ready карточка снова доступна.
  useEffect(() => {
    if (!sendPrevizDepth || !shot) return
    const status = shot.previz?.depth_status
    if (status === "pending" || status === "processing") return
    if (shot.previz?.depth_current) return
    setSendPrevizDepth(false)
  }, [sendPrevizDepth, shot])

  const refreshShotReferenceInputs = useCallback(() => {
    fetchShotWorkspaceDetail(projectId, shotId)
      .then(({ shot: s }) => {
        const detail = s as unknown as Record<string, unknown>
        const raw = detail.shot_reference_inputs as StoryboardPromptShot["shot_reference_inputs"] | undefined
        const next = raw ?? { storyboard: [], video: [], audio: [], voice: [] }
        const nextSeg = detail.animatic_segment as StoryboardPromptShot["animatic_segment"] | null | undefined
        const nextPreviz = detail.previz as StoryboardPromptShot["previz"] | undefined
        const scaleFields = readScaleFieldsFromDetail(detail)
        setShot((prev) =>
          prev
            ? {
                ...prev,
                storyboard_preview_url: s.storyboard_preview_url ?? null,
                storyboard_full_url:
                  (detail.storyboard_full_url as string | null | undefined) ?? prev.storyboard_full_url,
                storyboard_download_url:
                  (detail.storyboard_download_url as string | null | undefined) ?? prev.storyboard_download_url,
                shooting_board_preview_url: (detail.shooting_board_preview_url as string | null | undefined) ?? null,
                shooting_board_version_count:
                  (detail.shooting_board_version_count as number | undefined) ?? prev.shooting_board_version_count,
                reference_images: (detail.reference_images as ReferenceImage[]) ?? prev.reference_images,
                shot_reference_inputs: next,
                animatic_segment: nextSeg === undefined ? prev.animatic_segment : nextSeg,
                previz: nextPreviz === undefined ? prev.previz : nextPreviz,
                character_ids: scaleFields.character_ids,
                props: scaleFields.props,
                scale_applicable: scaleFields.scale_applicable,
                scale_sheet_applicable: scaleFields.scale_sheet_applicable,
                scale_sheet_figures_incomplete: scaleFields.scale_sheet_figures_incomplete,
                video_consider_scale: scaleFields.video_consider_scale,
                prop_scale_applicable: scaleFields.prop_scale_applicable,
                prop_scale_sheet_applicable: scaleFields.prop_scale_sheet_applicable,
                prop_scale_sheet_figures_incomplete: scaleFields.prop_scale_sheet_figures_incomplete,
                video_consider_prop_scale: scaleFields.video_consider_prop_scale,
                mixed_scale_applicable: scaleFields.mixed_scale_applicable,
                mixed_scale_sheet_applicable: scaleFields.mixed_scale_sheet_applicable,
                mixed_scale_sheet_figures_incomplete: scaleFields.mixed_scale_sheet_figures_incomplete,
              }
            : null,
        )
        setVideoConsiderScale(scaleFields.video_consider_scale)
        setVideoConsiderPropScale(scaleFields.video_consider_prop_scale)
        savedSnapshot.current.videoConsiderScale = scaleFields.video_consider_scale
        savedSnapshot.current.videoConsiderPropScale = scaleFields.video_consider_prop_scale
      })
      .catch(() => {})
  }, [projectId, shotId])

  const handleConfirmStoryboardSketch = useCallback(
    async (file: File) => {
      await uploadShotStoryboard(projectId, shotId, file)
      onDataChanged()
      refreshShotReferenceInputs()
    },
    [projectId, shotId, onDataChanged, refreshShotReferenceInputs],
  )

  const sketchEdit = useMemo(
    () => ({
      available: Boolean(sketch?.available),
      modelLabel: sketch?.modelLabel ?? "",
      modelKey: sketch?.modelKey,
    }),
    [sketch],
  )

  const storyboardSketchUrl = shot?.storyboard_download_url?.trim() || null
  const storyboardSketchPendingStatus = useWorkspaceJobsStore((s) => {
    if (storyboardSketchPendingJobId == null) return null
    return s.jobs.find((j) => j.id === storyboardSketchPendingJobId)?.status ?? null
  })

  useEffect(() => {
    if (
      storyboardSketchPendingStatus !== "completed" &&
      storyboardSketchPendingStatus !== "failed" &&
      storyboardSketchPendingStatus !== "cancelled"
    ) {
      return
    }
    setStoryboardSketchPendingJobId(null)
  }, [storyboardSketchPendingStatus])

  const handleStoryboardSketchPendingChange = useCallback(({ jobId, active }: SketchCorrectionPendingChange) => {
    setStoryboardSketchPendingJobId((current) => {
      if (active) return jobId
      return current === jobId ? null : current
    })
  }, [])

  // Soft-refresh media when preview side updates storyboard/video without remounting this view.
  useEffect(() => {
    if (refreshKey === 0) return
    refreshShotReferenceInputs()
  }, [refreshKey, refreshShotReferenceInputs])

  const tpl = shot ? norm(shot.storyboard_system_prompt_default) : ""
  const imgDef = shot ? norm(shot.storyboard_image_prompt_default) : ""
  const videoDef = shot ? norm(shot.video_kling_prompt_default) : ""
  const videoSysDef = shot ? norm(shot.video_system_prompt_default) : ""
  const shootingBoardDef = shot ? norm(shot.shooting_board_image_prompt_default) : ""
  const hasShootingBoard = shot
    ? Boolean(shot.shooting_board_preview_url?.trim()) || (shot.shooting_board_version_count ?? 0) > 0
    : false
  const shootingBoardCheckboxEnabled = isSeedanceVideo && hasShootingBoard
  const dirty =
    norm(system) !== savedSnapshot.current.system ||
    norm(image) !== savedSnapshot.current.image ||
    norm(videoText) !== savedSnapshot.current.videoText ||
    norm(videoSystem) !== savedSnapshot.current.videoSystem ||
    norm(shootingBoardText) !== savedSnapshot.current.shootingBoardText

  const saveButtonLabel = saving
    ? t("common.saving")
    : saveStatus === "error"
      ? t("common.saveErrorRetry")
      : dirty
        ? t("common.save")
        : saveStatus === "saved"
          ? t("common.savedState")
          : t("common.save")

  const toggleBlock = (block: string | null, include: boolean, setter: (v: boolean) => void) => {
    if (!block) return
    const trimmed = block.trim()
    if (include) {
      setImage((prev) => (prev.includes(trimmed) ? prev : `${prev.trimEnd()}\n\n${trimmed}`))
    } else {
      setImage((prev) =>
        prev
          .replace(trimmed, "")
          .replace(/\n{3,}/g, "\n\n")
          .trim(),
      )
    }
    setter(include)
  }

  const buildSettingsBody = useCallback((): PatchShotPromptSettingsBody => {
    const body: PatchShotPromptSettingsBody = {}
    if (supportsAnimaticReference) {
      const sendAnimatic = sendAnimaticRef && Boolean(shot?.animatic_segment)
      body.seedance_send_animatic_reference = sendAnimatic
      if (sendAnimatic) body.seedance_animatic_reference_mode = animaticRefMode
      body.seedance_send_previz_reference = sendPrevizRef && !previzRefTooLong && Boolean(shot?.previz)
      body.seedance_send_previz_depth = sendPrevizDepth
      body.seedance_previz_depth_variant = previzDepthVariant
    }
    if (isSeedanceVideo) {
      body.seedance_use_shooting_board = hasShootingBoard && useShootingBoard
    }
    if (!isTransition) {
      body.storyboard_send_current_as_reference = sendCurrentAsRef
      body.storyboard_animatic_reference_frame = animaticRefFrame ?? ""
    }
    body.video_send_storyboard_frame = sendStoryboardFrame
    if (shot?.mixed_scale_applicable) {
      body.video_consider_scale = videoConsiderScale
    } else {
      if (shot?.scale_applicable) {
        body.video_consider_scale = videoConsiderScale
      }
      if (shot?.prop_scale_applicable) {
        body.video_consider_prop_scale = videoConsiderPropScale
      }
    }
    return body
  }, [
    supportsAnimaticReference,
    sendAnimaticRef,
    animaticRefMode,
    sendPrevizRef,
    sendPrevizDepth,
    previzDepthVariant,
    previzRefTooLong,
    isSeedanceVideo,
    hasShootingBoard,
    useShootingBoard,
    isTransition,
    sendCurrentAsRef,
    animaticRefFrame,
    sendStoryboardFrame,
    videoConsiderScale,
    videoConsiderPropScale,
    shot?.mixed_scale_applicable,
    shot?.scale_applicable,
    shot?.prop_scale_applicable,
    shot?.animatic_segment,
    shot?.previz,
  ])

  // Мгновенное сохранение настроек экрана промпта (всё, кроме текста промптов).
  useEffect(() => {
    if (!shot) return
    const snap = savedSnapshot.current
    const presetsDiff = JSON.stringify(selectedPresetIds) !== JSON.stringify(snap.presetIds)
    const extrasDiff =
      sendAnimaticRef !== snap.sendAnimaticRef ||
      sendPrevizRef !== snap.sendPrevizRef ||
      sendPrevizDepth !== snap.sendPrevizDepth ||
      previzDepthVariant !== snap.previzDepthVariant ||
      animaticRefMode !== snap.animaticRefMode ||
      useShootingBoard !== snap.useShootingBoard ||
      sendStoryboardFrame !== snap.sendStoryboardFrame ||
      videoConsiderScale !== snap.videoConsiderScale ||
      videoConsiderPropScale !== snap.videoConsiderPropScale ||
      sendCurrentAsRef !== snap.sendCurrentAsRef ||
      animaticRefFrame !== snap.animaticRefFrame
    if (!presetsDiff && !extrasDiff) return

    const timer = setTimeout(() => {
      if (presetsDiff) {
        const nextPresets = [...selectedPresetIds]
        runWorkspace(
          projectId,
          "update_shot_presets",
          { shot_id: shot.id, camera_preset_ids: nextPresets },
          {
            onSuccess: () => {
              savedSnapshot.current.presetIds = nextPresets
            },
          },
        )
      }

      if (extrasDiff) {
        const nextSnap = {
          sendAnimaticRef,
          sendPrevizRef,
          sendPrevizDepth,
          previzDepthVariant,
          animaticRefMode,
          useShootingBoard,
          sendStoryboardFrame,
          videoConsiderScale,
          videoConsiderPropScale,
          sendCurrentAsRef,
          animaticRefFrame,
        }
        const seq = ++extrasSaveSeqRef.current
        void patchShotPromptSettings(projectId, shot.id, buildSettingsBody())
          .then(() => {
            if (seq !== extrasSaveSeqRef.current) return
            Object.assign(savedSnapshot.current, nextSnap)
            setShot((prev) => {
              if (!prev) return null
              const nextExtras: Record<string, unknown> = { ...(prev.llm_extras ?? {}) }
              nextExtras.video_send_storyboard_frame = sendStoryboardFrame
              if (!isTransition) {
                nextExtras.storyboard_send_current_as_reference = sendCurrentAsRef
                if (animaticRefFrame) {
                  nextExtras.storyboard_animatic_reference_frame = animaticRefFrame
                } else {
                  delete nextExtras.storyboard_animatic_reference_frame
                }
              }
              if (supportsAnimaticReference) {
                const sendAnimatic = sendAnimaticRef && Boolean(shot.animatic_segment)
                nextExtras.seedance_send_animatic_reference = sendAnimatic
                if (sendAnimatic) nextExtras.seedance_animatic_reference_mode = animaticRefMode
                nextExtras.seedance_send_previz_reference = sendPrevizRef && !previzRefTooLong && Boolean(shot.previz)
                nextExtras.seedance_send_previz_depth = sendPrevizDepth
                nextExtras.seedance_previz_depth_variant = previzDepthVariant
              }
              if (isSeedanceVideo) {
                nextExtras.seedance_use_shooting_board = hasShootingBoard && useShootingBoard
              }
              if (shot.mixed_scale_applicable) {
                nextExtras.video_consider_scale = videoConsiderScale
                nextExtras.video_consider_prop_scale = videoConsiderScale
              } else {
                if (shot.scale_applicable) {
                  nextExtras.video_consider_scale = videoConsiderScale
                }
                if (shot.prop_scale_applicable) {
                  nextExtras.video_consider_prop_scale = videoConsiderPropScale
                }
              }
              return {
                ...prev,
                llm_extras: nextExtras,
                video_consider_scale: videoConsiderScale,
                video_consider_prop_scale: videoConsiderPropScale,
              }
            })
          })
          .catch((e: unknown) => {
            if (seq !== extrasSaveSeqRef.current) return
            setError(e instanceof Error ? e.message : t("common.loadSettingsError"))
          })
      }
    }, 300)

    extrasSaveTimerRef.current = timer
    return () => {
      clearTimeout(timer)
      if (extrasSaveTimerRef.current === timer) extrasSaveTimerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPresetIds,
    sendAnimaticRef,
    sendPrevizRef,
    sendPrevizDepth,
    previzDepthVariant,
    previzRefTooLong,
    animaticRefMode,
    useShootingBoard,
    sendStoryboardFrame,
    videoConsiderScale,
    videoConsiderPropScale,
    sendCurrentAsRef,
    animaticRefFrame,
    shot?.id,
  ])

  // Дебаунс-автосохранение текста промптов (без создания версии истории).
  useEffect(() => {
    if (!shot) return
    if (!dirty || saving) return
    const timer = setTimeout(() => {
      void persist(true)
    }, 800)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system, image, videoText, videoSystem, shootingBoardText, shot, dirty, saving])

  const saveSeed = () => {
    if (!shot || !isByteplusSeedance) return
    const seedVal = seedText.trim()
    if (seedVal === savedSnapshot.current.seedText) return
    if (seedVal !== "" && !/^\d+$/.test(seedVal)) {
      setError(t("common.seedMustBeInteger"))
      return
    }
    setError(null)
    void patchShotPromptSettings(projectId, shot.id, { seedance_video_seed: seedVal })
      .then(() => {
        savedSnapshot.current.seedText = seedVal
        const parsedSeed = seedVal === "" || seedVal === shot.id.toString() ? null : Number(seedVal)
        setShot((prev) => {
          if (!prev) return null
          const nextExtras: Record<string, unknown> = { ...(prev.llm_extras ?? {}) }
          if (parsedSeed == null) {
            delete nextExtras.seedance_video_seed
          } else {
            nextExtras.seedance_video_seed = parsedSeed
          }
          return { ...prev, seedance_video_seed: parsedSeed, llm_extras: nextExtras }
        })
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : t("common.saveSeedError"))
      })
  }

  const handleShootingBoardPanelCountChange = (panelCount: number) => {
    if (!shot) return
    void patchShotPromptSettings(projectId, shot.id, {
      shooting_board_panel_count: panelCount,
    })
      .then(() => {
        setShot((prev) => (prev ? { ...prev, shooting_board_panel_count: panelCount } : prev))
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : t("common.loadSettingsError"))
      })
  }

  const cameraLabel = selectedPresetIds[0]
    ? (availablePresets.find((p) => p.id === selectedPresetIds[0])?.name ??
      shot?.camera_preset_name ??
      t("promptEditor.segmentCamera"))
    : t("promptEditor.segmentCamera")

  const cameraSelect = (
    <ShotCameraSelect
      projectId={projectId}
      label={cameraLabel}
      presets={availablePresets}
      selectedIds={selectedPresetIds}
      onSelectedIdsChange={setSelectedPresetIds}
      open={cameraPickerOpen}
      onOpenChange={setCameraPickerOpen}
      onPresetsLoaded={handleCameraPresetsLoaded}
    />
  )

  const persist = useCallback(
    (skipVersion: boolean): Promise<boolean> => {
      const run = async (): Promise<boolean> => {
        if (!shot) return false

        const videoSnapshot = videoEditorRef.current?.getSnapshot()
        const imageSnapshot = imageEditorRef.current?.getSnapshot()
        const sentVideoText = videoSnapshot?.text ?? videoText
        const sentVideoDoc = videoSnapshot?.doc ?? videoDoc
        const sentImageText = imageSnapshot?.text ?? image
        const sentImageDoc = imageSnapshot?.doc ?? shot.storyboard_image_prompt_doc
        const sentSystem = system
        const sentVideoSystem = videoSystem
        const shootingBoardSnapshot = shootingBoardEditorRef.current?.getSnapshot()
        const sentShootingBoardText = shootingBoardSnapshot?.text ?? shootingBoardText
        const sentShootingBoardDoc = shootingBoardSnapshot?.doc ?? shootingBoardDoc

        // Без изменений — не PATCH и не onDataChanged (закрытие шот-панели
        // иначе гоняет полный Inertia reload и сбрасывает аниматик/таймлайн).
        const snap = savedSnapshot.current
        if (
          norm(sentSystem) === snap.system &&
          norm(sentImageText) === snap.image &&
          norm(sentVideoText) === snap.videoText &&
          norm(sentVideoSystem) === snap.videoSystem &&
          norm(sentShootingBoardText) === snap.shootingBoardText
        ) {
          return true
        }

        setSaving(true)
        setSaveStatus("saving")
        setError(null)

        try {
          const vidVal = norm(sentVideoText)
          const vidSysVal = norm(sentVideoSystem)
          const videoCustom = vidVal !== "" && vidVal !== videoDef
          const videoChanged = norm(sentVideoText) !== snap.videoText || norm(sentVideoSystem) !== snap.videoSystem
          const imageChanged = !isTransition && (norm(sentSystem) !== snap.system || norm(sentImageText) !== snap.image)
          const shootingBoardChanged = norm(sentShootingBoardText) !== snap.shootingBoardText
          const body: PatchShotWorkspacePromptsBody = {
            skip_prompt_version: skipVersion,
          }
          if (videoChanged) {
            body.video_kling_prompt = videoCustom ? vidVal : ""
            body.video_kling_prompt_doc = videoCustom ? sentVideoDoc : null
            body.video_system_prompt = vidSysVal === videoSysDef ? "" : vidSysVal
          }
          if (imageChanged) {
            const sysVal = norm(sentSystem)
            const imgVal = norm(sentImageText)
            const imgCustom = imgVal !== "" && imgVal !== imgDef
            body.storyboard_system_prompt = sysVal === tpl ? "" : sysVal
            body.storyboard_image_prompt = imgCustom ? imgVal : ""
            body.storyboard_image_prompt_doc = imgCustom ? sentImageDoc : null
          }
          if (shootingBoardChanged) {
            const sbVal = norm(sentShootingBoardText)
            const sbCustom = sbVal !== "" && sbVal !== shootingBoardDef
            body.shooting_board_image_prompt = sbCustom ? sbVal : ""
            body.shooting_board_image_prompt_doc = sbCustom ? sentShootingBoardDoc : null
          }

          await patchShotWorkspacePrompts(projectId, shot.id, body)
          onDataChanged()
          savedSnapshot.current.system = norm(sentSystem)
          savedSnapshot.current.image = norm(sentImageText)
          savedSnapshot.current.videoText = norm(sentVideoText)
          savedSnapshot.current.videoSystem = norm(sentVideoSystem)
          savedSnapshot.current.shootingBoardText = norm(sentShootingBoardText)

          const currentVideo = videoEditorRef.current?.getSnapshot()
          const currentImage = imageEditorRef.current?.getSnapshot()
          const currentShootingBoard = shootingBoardEditorRef.current?.getSnapshot()
          const changedWhileSaving =
            norm(currentVideo?.text ?? videoText) !== norm(sentVideoText) ||
            norm(currentImage?.text ?? image) !== norm(sentImageText) ||
            norm(currentShootingBoard?.text ?? shootingBoardText) !== norm(sentShootingBoardText) ||
            norm(system) !== norm(sentSystem) ||
            norm(videoSystem) !== norm(sentVideoSystem)
          setSaveStatus(changedWhileSaving ? "idle" : "saved")
          return true
        } catch (e) {
          setError(e instanceof Error ? e.message : t("common.loadSettingsError"))
          setSaveStatus("error")
          return false
        } finally {
          setSaving(false)
        }
      }

      return enqueueSave(saveQueueRef, run)
    },
    [
      shot,
      videoText,
      videoDoc,
      videoSystem,
      videoDef,
      videoSysDef,
      shootingBoardText,
      shootingBoardDoc,
      shootingBoardDef,
      isTransition,
      system,
      image,
      tpl,
      imgDef,
      projectId,
      onDataChanged,
      t,
    ],
  )
  persistRef.current = persist

  const applySendMotionReference = useCallback(
    async (which: "previz" | "animatic"): Promise<boolean> => {
      if (!shot || !supportsAnimaticReference) return false
      if (extrasSaveTimerRef.current != null) {
        clearTimeout(extrasSaveTimerRef.current)
        extrasSaveTimerRef.current = null
      }

      const sendPreviz = which === "previz"
      const sendAnimatic = which === "animatic"
      const body = buildSettingsBody()
      body.seedance_send_previz_reference = sendPreviz && !previzRefTooLong && Boolean(shot.previz)
      body.seedance_send_animatic_reference = sendAnimatic && Boolean(shot.animatic_segment)
      if (body.seedance_send_animatic_reference) body.seedance_animatic_reference_mode = animaticRefMode

      const seq = ++extrasSaveSeqRef.current
      try {
        await patchShotPromptSettings(projectId, shot.id, body)
      } catch (e: unknown) {
        if (seq === extrasSaveSeqRef.current) {
          setError(e instanceof Error ? e.message : t("common.loadSettingsError"))
        }
        return false
      }
      if (seq !== extrasSaveSeqRef.current) return false

      savedSnapshot.current.sendPrevizRef = sendPreviz
      savedSnapshot.current.sendAnimaticRef = sendAnimatic
      setSendPrevizRef(sendPreviz)
      setSendAnimaticRef(sendAnimatic)
      setShot((prev) => {
        if (!prev) return null
        const nextExtras: Record<string, unknown> = { ...(prev.llm_extras ?? {}) }
        nextExtras.seedance_send_previz_reference = sendPreviz && !previzRefTooLong && Boolean(prev.previz)
        nextExtras.seedance_send_animatic_reference = sendAnimatic && Boolean(prev.animatic_segment)
        if (sendAnimatic && prev.animatic_segment) nextExtras.seedance_animatic_reference_mode = animaticRefMode
        return { ...prev, llm_extras: nextExtras }
      })
      return true
    },
    [animaticRefMode, buildSettingsBody, previzRefTooLong, projectId, shot, supportsAnimaticReference, t],
  )

  const confirmMotionReferences = useCallback(async (): Promise<boolean> => {
    if (!supportsAnimaticReference || !shot) return true

    const previzOn = sendPrevizRef && Boolean(shot.previz) && !previzRefTooLong
    const animaticOn = sendAnimaticRef && Boolean(shot.animatic_segment)
    if (previzOn || animaticOn) return true

    if (shot.previz && !previzRefTooLong) {
      const choice = await confirmChoice({
        title: t("videoPrevizConfirm.title"),
        message: t("videoPrevizConfirm.message"),
        confirmLabel: t("videoPrevizConfirm.with"),
        alternativeLabel: t("videoPrevizConfirm.without"),
      })
      if (choice == null) return false
      if (choice === "confirm") return applySendMotionReference("previz")
    }

    if (shot.animatic_segment) {
      const choice = await confirmChoice({
        title: t("videoAnimaticConfirm.title"),
        message: t("videoAnimaticConfirm.message"),
        confirmLabel: t("videoAnimaticConfirm.with"),
        alternativeLabel: t("videoAnimaticConfirm.without"),
      })
      if (choice == null) return false
      if (choice === "confirm") return applySendMotionReference("animatic")
    }

    return true
  }, [applySendMotionReference, previzRefTooLong, sendAnimaticRef, sendPrevizRef, shot, supportsAnimaticReference, t])

  useImperativeHandle(
    ref,
    () => ({
      flushPromptSave: () => persist(true),
      confirmMotionReferences,
      applyExternalPrompt: ({ target, text, cameraPresetIds, panelCount }) => {
        const timers = agentUpdateTimersRef.current
        if (timers.raf1 != null) cancelAnimationFrame(timers.raf1)
        if (timers.raf2 != null) cancelAnimationFrame(timers.raf2)
        if (timers.hold != null) window.clearTimeout(timers.hold)

        setAgentUpdatingTarget(target)
        pendingExternalPromptRef.current = { target, text, cameraPresetIds }
        if (Array.isArray(cameraPresetIds) && cameraPresetIds.length > 0) {
          const known = new Set(availablePresets.map((p) => p.id))
          const ids =
            known.size > 0
              ? cameraPresetIds.filter((id) => known.has(id))
              : cameraPresetIds.filter((id) => Number.isFinite(id) && id > 0)
          if (ids.length > 0) {
            setSelectedPresetIds(ids)
            savedSnapshot.current.presetIds = ids
          }
        }

        timers.raf1 = requestAnimationFrame(() => {
          timers.raf2 = requestAnimationFrame(() => {
            if (target === "video") {
              const editor = videoEditorRef.current
              if (editor) {
                editor.setExternalText(text)
                const snap = editor.getSnapshot()
                const nextText = snap?.text ?? text
                setVideoText(nextText)
                setVideoDoc(snap?.doc ?? null)
                savedSnapshot.current.videoText = nextText
                pendingExternalPromptRef.current = null
              } else {
                // Assistant tab: editor unmounted — keep text, clear stale TipTap doc.
                setVideoText(text)
                setVideoDoc(null)
                savedSnapshot.current.videoText = text
              }
            } else if (target === "shooting_board") {
              const editor = shootingBoardEditorRef.current
              if (editor) {
                editor.setExternalText(text)
                const snap = editor.getSnapshot()
                const nextText = snap?.text ?? text
                setShootingBoardText(nextText)
                setShootingBoardDoc(snap?.doc ?? null)
                savedSnapshot.current.shootingBoardText = nextText
                pendingExternalPromptRef.current = null
              } else {
                setShootingBoardText(text)
                setShootingBoardDoc(null)
                savedSnapshot.current.shootingBoardText = text
              }
              const nextCount = panelCount ?? 0
              setShot((prev) =>
                prev
                  ? {
                      ...prev,
                      ...(nextCount >= SHOOTING_BOARD_PANEL_COUNT_MIN ? { shooting_board_panel_count: nextCount } : {}),
                      shooting_board_image_prompt: text,
                      shooting_board_image_prompt_doc: null,
                    }
                  : prev,
              )
            } else {
              const editor = imageEditorRef.current
              if (editor) {
                editor.setExternalText(text)
                pendingExternalPromptRef.current = null
              }
              setImage(text)
              savedSnapshot.current.image = text
              // Drop persisted doc so remount prefers valueText (agent text).
              setShot((prev) => (prev ? { ...prev, storyboard_image_prompt_doc: null } : prev))
            }
            timers.hold = window.setTimeout(() => {
              setAgentUpdatingTarget(null)
              timers.hold = undefined
            }, 400)
          })
        })
      },
      applyExternalSettings: (payload) => {
        if (payload.sendAnimatic !== undefined) {
          setSendAnimaticRef(payload.sendAnimatic)
          savedSnapshot.current.sendAnimaticRef = payload.sendAnimatic
        }
        if (payload.animaticMode !== undefined) {
          const mode = String(payload.animaticMode)
          if ((SEEDANCE_ANIMATIC_REF_MODES as string[]).includes(mode)) {
            const next = mode as SeedanceAnimaticRefMode
            setAnimaticRefMode(next)
            savedSnapshot.current.animaticRefMode = next
          }
        }
        if (payload.useShootingBoard !== undefined) {
          setUseShootingBoard(payload.useShootingBoard)
          savedSnapshot.current.useShootingBoard = payload.useShootingBoard
        }
      },
    }),
    [persist, availablePresets, confirmMotionReferences],
  )

  useEffect(() => {
    const timers = agentUpdateTimersRef.current
    return () => {
      if (timers.raf1 != null) cancelAnimationFrame(timers.raf1)
      if (timers.raf2 != null) cancelAnimationFrame(timers.raf2)
      if (timers.hold != null) window.clearTimeout(timers.hold)
    }
  }, [])

  // Flush agent prompt into TipTap once Frame/Video editor remounts after Assistant tab.
  useEffect(() => {
    if (!composerHost) return
    let cancelled = false
    const tryFlush = () => {
      if (cancelled) return
      const pending = pendingExternalPromptRef.current
      if (!pending) return
      if (composerKind !== composerKindForExternalPrompt(pending.target)) return
      const editor = {
        video: videoEditorRef,
        shooting_board: shootingBoardEditorRef,
        image: imageEditorRef,
      }[pending.target].current
      if (!editor) {
        requestAnimationFrame(tryFlush)
        return
      }
      editor.setExternalText(pending.text)
      if (pending.target === "video") {
        const snap = editor.getSnapshot()
        const nextText = snap?.text ?? pending.text
        setVideoText(nextText)
        setVideoDoc(snap?.doc ?? null)
        savedSnapshot.current.videoText = nextText
      } else if (pending.target === "shooting_board") {
        const snap = editor.getSnapshot()
        const nextText = snap?.text ?? pending.text
        setShootingBoardText(nextText)
        setShootingBoardDoc(snap?.doc ?? null)
        savedSnapshot.current.shootingBoardText = nextText
      } else {
        setImage(pending.text)
        savedSnapshot.current.image = pending.text
      }
      pendingExternalPromptRef.current = null
    }
    tryFlush()
    return () => {
      cancelled = true
    }
  }, [composerHost, composerKind])

  const save = useCallback(() => {
    void persist(false)
  }, [persist])

  const composerActionsFor = (editorRef: RefObject<PromptEditorHandle | null>) => (
    <div className="absolute right-1 bottom-1 z-10 flex items-center gap-3">
      <VoiceDictationButton projectId={projectId} onText={(text) => editorRef.current?.insertTextAtCursor(text)} />
      <Button type="button" size="sm" className="shadow-lg" disabled={saving} onClick={save}>
        {saveButtonLabel}
      </Button>
    </div>
  )

  const extrasNode =
    shot && !loading && !error ? (
      previewTab === "storyboard" && !isTransition ? (
        <ShotStoryboardGenerationExtras
          storyboardPreviewUrl={shot.storyboard_preview_url}
          sendCurrentAsRef={sendCurrentAsRef}
          onSendCurrentAsRefChange={setSendCurrentAsRef}
          hasAnimaticSegment={Boolean(shot.animatic_segment)}
          animaticRefFrame={animaticRefFrame}
          onAnimaticRefFrameChange={setAnimaticRefFrame}
          animaticFrames={
            shot.animatic_segment
              ? [
                  {
                    key: "first" as const,
                    label: t("common.first"),
                    url: shot.animatic_segment.first_frame_url,
                    ok: shot.animatic_segment.first_frame_status === "ready",
                  },
                  {
                    key: "poster" as const,
                    label: t("common.poster"),
                    url: shot.animatic_segment.poster_url,
                    ok: shot.animatic_segment.poster_status === "ready",
                  },
                  {
                    key: "last" as const,
                    label: t("common.last"),
                    url: shot.animatic_segment.last_frame_url,
                    ok: shot.animatic_segment.last_frame_status === "ready",
                  },
                ]
              : []
          }
          includeLocation={includeLocation}
          includeCharacters={includeCharacters}
          includeProps={includeProps}
          locationBlock={shot.storyboard_location_block}
          charactersBlock={shot.storyboard_characters_block}
          propsBlock={shot.storyboard_props_block}
          onToggleBlock={(block, include, kind) => {
            if (kind === "location") toggleBlock(block, include, setIncludeLocation)
            else if (kind === "characters") toggleBlock(block, include, setIncludeCharacters)
            else toggleBlock(block, include, setIncludeProps)
          }}
          providerLabel={storyboardEngineLabel}
          systemOpen={systemOpen}
          onSystemOpenChange={setSystemOpen}
          systemPrompt={system}
          onSystemPromptChange={setSystem}
        />
      ) : previewTab === "video" ? (
        <ShotVideoGenerationExtras
          markers={shot.active_video_timeline_markers ?? []}
          supportsAnimaticReference={supportsAnimaticReference}
          hasAnimaticSegment={Boolean(shot.animatic_segment)}
          sendAnimaticRef={sendAnimaticRef}
          onSendAnimaticRefChange={handleSendAnimaticRefChange}
          animaticRefMode={animaticRefMode}
          onAnimaticRefModeChange={setAnimaticRefMode}
          videoEngineLabel={videoEngineLabel}
          animaticSegment={shot.animatic_segment ?? null}
          maxVideoRefSeconds={maxAnimaticRefSeconds}
          previzMaxSeconds={previzMaxSeconds}
          hasLinkedPreviz={Boolean(shot.previz)}
          sendPrevizRef={sendPrevizRef}
          onSendPrevizRefChange={handleSendPrevizRefChange}
          sendPrevizDepth={sendPrevizDepth}
          onSendPrevizDepthChange={setSendPrevizDepth}
          previzDepthVariant={previzDepthVariant}
          onPrevizDepthVariantChange={setPrevizDepthVariant}
          previzDepthReady={Boolean(shot.previz?.depth_current)}
          previzDepthStatus={shot.previz?.depth_status ?? null}
          previzStatus={shot.previz?.status ?? null}
          previzRefTooLong={previzRefTooLong}
          previzDurationSec={previzDurationSec}
          scaleApplicable={scaleApplicable}
          scaleSheetLinkEnabled={scaleSheetLinkEnabled}
          videoConsiderScale={videoConsiderScale}
          onVideoConsiderScaleChange={setVideoConsiderScale}
          onOpenScaleSheet={() => {
            void (async () => {
              if (persistRef.current) await persistRef.current(true)
              setScaleSheetOpen(true)
            })()
          }}
          propScaleApplicable={propScaleApplicable}
          propScaleSheetLinkEnabled={propScaleSheetLinkEnabled}
          videoConsiderPropScale={videoConsiderPropScale}
          onVideoConsiderPropScaleChange={setVideoConsiderPropScale}
          onOpenPropScaleSheet={() => {
            void (async () => {
              if (persistRef.current) await persistRef.current(true)
              setPropScaleSheetOpen(true)
            })()
          }}
          mixedScaleApplicable={mixedScaleApplicable}
          mixedScaleSheetLinkEnabled={mixedScaleSheetLinkEnabled}
          onOpenMixedScaleSheet={() => {
            void (async () => {
              if (persistRef.current) await persistRef.current(true)
              setMixedScaleSheetOpen(true)
            })()
          }}
          scaleSheetFiguresIncomplete={Boolean(shot.scale_sheet_figures_incomplete)}
          propScaleSheetFiguresIncomplete={Boolean(shot.prop_scale_sheet_figures_incomplete)}
          mixedScaleSheetFiguresIncomplete={Boolean(shot.mixed_scale_sheet_figures_incomplete)}
          shootingBoardCheckboxEnabled={shootingBoardCheckboxEnabled}
          isSeedanceVideo={isSeedanceVideo}
          hasShootingBoard={hasShootingBoard}
          useShootingBoard={useShootingBoard}
          onUseShootingBoardChange={setUseShootingBoard}
          isByteplusSeedance={isByteplusSeedance}
          seedText={seedText}
          onSeedTextChange={setSeedText}
          onSeedBlur={saveSeed}
          shotId={shot.id}
          videoSystemOpen={videoSystemOpen}
          onVideoSystemOpenChange={setVideoSystemOpen}
          videoSystem={videoSystem}
          onVideoSystemChange={setVideoSystem}
        />
      ) : previewTab === "animatic" ? (
        <ShotAnimaticTranscriptionExtras
          projectId={projectId}
          shotId={shotId}
          hasSegment={Boolean(shot.animatic_segment)}
          transcribing={transcribing}
          transcriptionError={transcriptionError}
          transcriptionRefreshKey={transcriptionRefreshKey}
          onTranscribe={() => void handleTranscribe()}
          onDataChanged={onDataChanged}
        />
      ) : null
    ) : null

  const composerNode = (
    <div className={cn("flex min-h-0 flex-col", fillColumn ? "h-full flex-1" : "h-full")}>
      {loading ? (
        fillColumn ? (
          <PromptFieldLoadingFrame
            className="px-1 pb-1"
            label={
              composerKind === "video"
                ? t("common.videoPrompt")
                : composerKind === "shooting_board"
                  ? t("common.shootingBoardPrompt")
                  : t("common.framePrompt")
            }
            message={t("common.loadingPrompts")}
          />
        ) : (
          <p className="mt-6 text-sm text-ui-text-secondary">{t("common.loadingPrompts")}</p>
        )
      ) : null}
      {error ? (
        <p className="mt-6 text-sm text-ui-status-failed" role="alert">
          {error}
        </p>
      ) : null}
      {shot && !loading && !error ? (
        <>
          <div
            className={cn(
              "min-h-0 flex-1 px-1",
              fillColumn ? "flex flex-col overflow-hidden pb-1" : "it2tv-scrollbar overflow-y-auto",
            )}
          >
            {composerKind === "storyboard" && isTransition ? (
              <p className="rounded-lg border border-dashed border-ui-border-strong bg-ui-surface-sunken px-3 py-6 text-center text-xs text-ui-text-muted">
                {t("composerTabs.transitionEmpty")}
              </p>
            ) : null}
            {composerKind === "storyboard" && !isTransition && (
              <div className={cn(fillColumn ? "flex min-h-0 flex-1 flex-col gap-4" : "space-y-4")}>
                <ShotReferenceInputsPanel
                  className={fillColumn ? "shrink-0" : undefined}
                  projectId={projectId}
                  shotId={shotId}
                  kind="storyboard"
                  items={shot.shot_reference_inputs.storyboard}
                  promptWatchText={`${system}\n${image}`}
                  onChanged={refreshShotReferenceInputs}
                  onInsertLabel={(label) => imageEditorRef.current?.insertReference(label)}
                />
                <div className={cn(fillColumn && "flex min-h-[140px] flex-1 flex-col")}>
                  <div className={cn("flex items-center justify-between gap-2", fillColumn && "shrink-0")}>
                    <label className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
                      {t("common.framePrompt")}
                    </label>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-7 gap-1.5 px-2 text-[11px]"
                        title={t("common.promptHistory")}
                        onClick={() => setPromptHistoryKind("storyboard")}
                      >
                        <History className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className={cn("relative mt-1.5", fillColumn && "flex min-h-0 flex-1 flex-col")}>
                    <PromptEditor
                      ref={imageEditorRef}
                      valueText={image}
                      valueDoc={shot.storyboard_image_prompt_doc}
                      seedKey={shot.id}
                      mentionItems={storyboardMentionItems}
                      cameraPresets={availablePresets}
                      cameraProjectId={projectId}
                      onCameraPresetsLoaded={handleCameraPresetsLoaded}
                      onChange={(text) => setImage(text)}
                      toolbarExtra={cameraSelect}
                      placeholder={t("placeholders.framePrompt")}
                      showMentionHint={false}
                      fillAvailable={fillColumn}
                      className={fillColumn ? "min-h-0 flex-1" : undefined}
                    />
                    <AnimatePresence>
                      {agentUpdatingTarget === "image" ? (
                        <motion.div
                          key="agent-prompt-overlay-image"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-ui-bg-elevated/70 backdrop-blur-[2px]"
                        >
                          <div className="flex items-center gap-2 rounded-lg border border-ui-border bg-ui-surface px-3 py-1.5 text-xs text-ui-text-primary shadow-md">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            {t("workspace:agent.updatingPrompt")}
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                    {composerActionsFor(imageEditorRef)}
                  </div>
                </div>
              </div>
            )}

            {composerKind === "video" && (
              <div className={cn(fillColumn ? "flex min-h-0 flex-1 flex-col gap-4" : "space-y-4")}>
                <ShotReferenceInputsPanel
                  className={fillColumn ? "shrink-0" : undefined}
                  projectId={projectId}
                  shotId={shotId}
                  kind="video"
                  items={[
                    ...[...(shot.shot_reference_inputs.video ?? []), ...(shot.shot_reference_inputs.audio ?? [])].sort(
                      (a, b) => a.position - b.position,
                    ),
                    ...(shot.shot_reference_inputs.voice ?? []),
                  ]}
                  promptWatchText={`${videoSystem}\n${videoText}`}
                  onChanged={refreshShotReferenceInputs}
                  acceptVideo
                  onInsertLabel={(label) => videoEditorRef.current?.insertReference(label)}
                  leadingItem={
                    shot.storyboard_preview_url ? (
                      <div className="flex flex-col gap-1">
                        <ReferenceInputThumb
                          url={shot.storyboard_preview_url}
                          cornerAction="visibility"
                          visible={sendStoryboardFrame}
                          onToggleVisibility={() => setSendStoryboardFrame((v) => !v)}
                          onOpenPreview={() => setFramePreviewOpen(true)}
                          openPreviewTitle={t("tabs.storyboard")}
                          visibilityOnAriaLabel={t("referenceInputs.sendStoryboardFrame")}
                          visibilityOffAriaLabel={t("referenceInputs.skipStoryboardFrame")}
                          onSketchEdit={
                            storyboardSketchUrl
                              ? () => {
                                  setSketchOpen(true)
                                }
                              : undefined
                          }
                          sketchEditing={storyboardSketchPendingJobId != null}
                          sketchEditingLabel={t("canvas.sketch.correctionShort", { ns: "workspace" })}
                          sketchEditAriaLabel={t("canvas.sketch.editInSketch", { ns: "workspace" })}
                        />
                        <span className="max-w-[5.5rem] truncate text-center text-[10px] text-ui-text-muted">
                          {t("tabs.storyboard")}
                        </span>
                      </div>
                    ) : undefined
                  }
                />
                <div className={cn(fillColumn && "flex min-h-[140px] flex-1 flex-col")}>
                  <div className={cn("flex items-center justify-between gap-2", fillColumn && "shrink-0")}>
                    <label className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
                      {t("common.videoPrompt")}
                    </label>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-7 gap-1.5 px-2 text-[11px]"
                        title={t("common.promptHistory")}
                        onClick={() => setPromptHistoryKind("video")}
                      >
                        <History className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className={cn("relative mt-1.5", fillColumn && "flex min-h-0 flex-1 flex-col")}>
                    <PromptEditor
                      ref={videoEditorRef}
                      valueText={videoText}
                      valueDoc={videoDoc}
                      seedKey={shot.id}
                      mentionItems={videoMentionItems}
                      cameraPresets={availablePresets}
                      cameraProjectId={projectId}
                      onCameraPresetsLoaded={handleCameraPresetsLoaded}
                      onChange={(text, doc) => {
                        setVideoText(text)
                        setVideoDoc(doc)
                      }}
                      toolbarExtra={cameraSelect}
                      placeholder={t("placeholders.videoPrompt")}
                      showMentionHint={false}
                      fillAvailable={fillColumn}
                      className={fillColumn ? "min-h-0 flex-1" : undefined}
                    />
                    <AnimatePresence>
                      {agentUpdatingTarget === "video" ? (
                        <motion.div
                          key="agent-prompt-overlay-video"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-ui-bg-elevated/70 backdrop-blur-[2px]"
                        >
                          <div className="flex items-center gap-2 rounded-lg border border-ui-border bg-ui-surface px-3 py-1.5 text-xs text-ui-text-primary shadow-md">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            {t("workspace:agent.updatingPrompt")}
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                    {composerActionsFor(videoEditorRef)}
                  </div>
                </div>
              </div>
            )}
            {composerKind === "shooting_board" ? (
              <div className={cn(fillColumn ? "flex min-h-0 flex-1 flex-col gap-4" : "space-y-4")}>
                <div className={cn(fillColumn && "shrink-0")}>
                  <ShootingBoardPanelCountPicker
                    panelCount={shot.shooting_board_panel_count}
                    disabled={saving}
                    onChange={handleShootingBoardPanelCountChange}
                  />
                </div>
                <div className={cn(fillColumn && "flex min-h-[140px] flex-1 flex-col")}>
                  <label
                    className={cn(
                      "text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase",
                      fillColumn && "shrink-0",
                    )}
                  >
                    {t("common.shootingBoardPrompt")}
                  </label>
                  <div className={cn("relative mt-1.5", fillColumn && "flex min-h-0 flex-1 flex-col")}>
                    <PromptEditor
                      ref={shootingBoardEditorRef}
                      valueText={shootingBoardText}
                      valueDoc={shootingBoardDoc}
                      seedKey={shot.id}
                      mentionItems={storyboardMentionItems}
                      cameraPresets={availablePresets}
                      cameraProjectId={projectId}
                      onCameraPresetsLoaded={handleCameraPresetsLoaded}
                      onChange={(text, doc) => {
                        setShootingBoardText(text)
                        setShootingBoardDoc(doc)
                      }}
                      toolbarExtra={cameraSelect}
                      placeholder={t("placeholders.shootingBoardPrompt")}
                      showMentionHint={false}
                      fillAvailable={fillColumn}
                      className={fillColumn ? "min-h-0 flex-1" : undefined}
                    />
                    <AnimatePresence>
                      {agentUpdatingTarget === "shooting_board" ? (
                        <motion.div
                          key="agent-prompt-overlay-shooting-board"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-ui-bg-elevated/70 backdrop-blur-[2px]"
                        >
                          <div className="flex items-center gap-2 rounded-lg border border-ui-border bg-ui-surface px-3 py-1.5 text-xs text-ui-text-primary shadow-md">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            {t("workspace:agent.updatingPrompt")}
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                    {composerActionsFor(shootingBoardEditorRef)}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  )

  if (!project) return null

  return (
    <>
      {composerHost ? createPortal(composerNode, composerHost) : null}
      {extrasHost && extrasNode ? createPortal(extrasNode, extrasHost) : null}
      {framePreviewOpen && shot && storyboardSketchUrl ? (
        <SketchDialog
          open
          onOpenChange={(open) => {
            if (!open) setFramePreviewOpen(false)
          }}
          projectId={projectId}
          projectFrameFormat={project.frame_format}
          sketchEdit={sketchEdit}
          sourceUrl={storyboardSketchUrl}
          initialFileName={`storyboard_shot${shot.number}`}
          headerTitle={t("tabs.storyboard")}
          presentation="view"
          persist="confirm"
          confirmAsSave
          nested
          onConfirm={handleConfirmStoryboardSketch}
          onCorrectionPendingChange={handleStoryboardSketchPendingChange}
          replaceShotOnCorrect={{ shotId: shot.id, tab: "storyboard" }}
        />
      ) : null}
      {framePreviewOpen && shot && !storyboardSketchUrl && shot.storyboard_preview_url ? (
        <ImageLightboxDialog
          open
          onOpenChange={(open) => {
            if (!open) setFramePreviewOpen(false)
          }}
          url={shot.storyboard_preview_url}
          title={t("tabs.storyboard")}
          downloadUrl={shot.storyboard_preview_url}
          downloadFilename={`storyboard_shot${shot.number}.png`}
          copyable
          nested
        />
      ) : null}
      {promptHistoryKind ? (
        <PromptVersionsDialog
          projectId={projectId}
          shotId={shotId}
          kind={promptHistoryKind}
          open
          onOpenChange={(open) => {
            if (!open) setPromptHistoryKind(null)
          }}
          onPick={(systemPrompt, userPrompt) => {
            if (promptHistoryKind === "storyboard") {
              setSystem(systemPrompt)
              imageEditorRef.current?.setExternalText(userPrompt)
              setImage(userPrompt)
            } else {
              setVideoSystem(systemPrompt)
              videoEditorRef.current?.setExternalText(userPrompt)
              const snap = videoEditorRef.current?.getSnapshot()
              setVideoText(snap?.text ?? userPrompt)
              setVideoDoc(snap?.doc ?? null)
            }
          }}
          onDataChanged={onDataChanged}
        />
      ) : null}
      <ProjectScaleSheetModal
        projectId={projectId}
        open={scaleSheetOpen}
        onOpenChange={setScaleSheetOpen}
        shotId={shot?.id}
        characterIds={linkedCharacterIds}
        variant="shot"
        onApplied={onDataChanged}
      />
      <PropScaleSheetModal
        projectId={projectId}
        open={propScaleSheetOpen}
        onOpenChange={setPropScaleSheetOpen}
        shotId={shot?.id}
        propIds={linkedPropIds}
        variant="shot"
        onApplied={onDataChanged}
      />
      <MixedScaleSheetModal
        projectId={projectId}
        open={mixedScaleSheetOpen}
        onOpenChange={setMixedScaleSheetOpen}
        shotId={shot?.id ?? null}
        nested
        onApplied={onDataChanged}
      />
      {shot && storyboardSketchUrl ? (
        <SketchDialog
          open={sketchOpen}
          onOpenChange={setSketchOpen}
          projectId={projectId}
          projectFrameFormat={project.frame_format}
          sketchEdit={sketchEdit}
          sourceUrl={storyboardSketchUrl}
          headerTitle={t("tabs.storyboard")}
          presentation="edit"
          persist="confirm"
          confirmAsSave
          nested
          onConfirm={handleConfirmStoryboardSketch}
          onCorrectionPendingChange={handleStoryboardSketchPendingChange}
          replaceShotOnCorrect={{ shotId: shot.id, tab: "storyboard" }}
        />
      ) : null}
    </>
  )
})
